import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useCustomsCases, type CustomsCase } from '../../../hooks/useCustomsCases'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  awaiting_response: 'Awaiting Response',
  c18_received: 'C18 Received',
  appealing: 'Appealing',
  paid: 'Paid',
  closed: 'Closed',
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-gray-100 text-gray-700',
  awaiting_response: 'bg-yellow-100 text-yellow-800',
  c18_received: 'bg-orange-100 text-orange-800',
  appealing: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-600',
}

const ACTION_TYPE_STYLES: Record<string, string> = {
  initial_email_sent: 'bg-blue-100 text-blue-800',
  reminder_sent: 'bg-blue-100 text-blue-800',
  c18_received: 'bg-orange-100 text-orange-800',
  appeal_submitted: 'bg-purple-100 text-purple-800',
  paid: 'bg-green-100 text-green-800',
  other: 'bg-gray-100 text-gray-700',
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  initial_email_sent: 'Initial Email Sent',
  reminder_sent: 'Reminder Sent',
  c18_received: 'C18 Received',
  appeal_submitted: 'Appeal Submitted',
  paid: 'Paid',
  other: 'Other',
}

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

interface POA {
  id: string
  agent_name: string
  signatory_name: string
  valid_until: string
  status: string
}

export default function CaseDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { fetchCase, updateCase, fetchNotes, addNote } = useCustomsCases()

  const [caseData, setCaseData] = useState<CustomsCase | null>(null)
  const [notes, setNotes] = useState<any[]>([])
  const [poa, setPoa] = useState<POA | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState<Partial<CustomsCase>>({})

  const [noteText, setNoteText] = useState('')
  const [noteActionType, setNoteActionType] = useState('other')
  const [addingNote, setAddingNote] = useState(false)

  useEffect(() => {
    const loadCaseData = async () => {
      if (!id) return
      setLoading(true)
      setError(null)

      try {
        const caseRec = await fetchCase(id)
        setCaseData(caseRec)
        setEditForm(caseRec)

        const caseNotes = await fetchNotes(id)
        setNotes(caseNotes)

        // Fetch matching POA by EORI
        if (caseRec.importer_eori) {
          const { data: poaRec } = await supabase
            .from('poas')
            .select('id, agent_name, signatory_name, valid_until, status')
            .eq('eori_number', caseRec.importer_eori)
            .maybeSingle()
          if (poaRec) {
            setPoa(poaRec as POA)
          }
        }
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }

    loadCaseData()
  }, [id, fetchCase, fetchNotes])

  const handleStatusChange = async (newStatus: string) => {
    if (!id || !caseData) return
    try {
      const updated = await updateCase(id, { status: newStatus })
      setCaseData(updated)
    } catch (e: any) {
      alert('Error: ' + e.message)
    }
  }

  const handleSave = async () => {
    if (!id) return
    setSaving(true)
    try {
      const updated = await updateCase(id, editForm)
      setCaseData(updated)
      setEditing(false)
    } catch (e: any) {
      alert('Error: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleAddNote = async () => {
    if (!id || !noteText.trim()) return
    setAddingNote(true)
    try {
      await addNote(id, noteText, noteActionType, user?.email || null)
      setNoteText('')
      setNoteActionType('other')
      const updatedNotes = await fetchNotes(id)
      setNotes(updatedNotes)
    } catch (e: any) {
      alert('Error: ' + e.message)
    } finally {
      setAddingNote(false)
    }
  }

  if (loading) return <div className="p-6">Loading...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>
  if (!caseData) return <div className="p-6">Case not found</div>

  const field = (label: string, key: keyof CustomsCase, type = 'text') => (
    <div className="mb-4">
      <label className={LABEL_CLS}>{label}</label>
      {editing ? (
        type === 'select' ? (
          <select
            className={INPUT_CLS}
            value={(editForm[key] as string) || ''}
            onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
          >
            <option value="">-</option>
            {['open', 'awaiting_response', 'c18_received', 'appealing', 'paid', 'closed'].map(s => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            className={INPUT_CLS}
            rows={3}
            value={(editForm[key] as string) || ''}
            onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
          />
        ) : (
          <input
            type={type}
            className={INPUT_CLS}
            value={(editForm[key] as string) || ''}
            onChange={e =>
              setEditForm(f => ({
                ...f,
                [key]: type === 'number' ? (e.target.value ? parseFloat(e.target.value) : null) : e.target.value,
              }))
            }
          />
        )
      ) : (
        <div className="text-gray-900 bg-gray-50 rounded px-3 py-2 min-h-[38px]">
          {caseData[key] != null ? String(caseData[key]) : <span className="text-gray-400">-</span>}
        </div>
      )}
    </div>
  )

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Link to="/customs/letters" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
        ← Back
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold flex-1">{caseData.cfss_number || 'CFSS Case'}</h1>
        <select
          className={`px-3 py-1 rounded text-sm ${STATUS_STYLES[caseData.status] || STATUS_STYLES.open}`}
          value={caseData.status}
          onChange={e => handleStatusChange(e.target.value)}
        >
          {['open', 'awaiting_response', 'c18_received', 'appealing', 'paid', 'closed'].map(s => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        {editing ? (
          <>
            <button onClick={() => setEditing(false)} className="px-4 py-2 border rounded">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} className="px-4 py-2 border rounded">
            Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* CFSS Details */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="font-semibold mb-4 text-gray-700">CFSS Details</h2>
          {field('CFSS Number', 'cfss_number')}
          {field('Initial Letter Date', 'initial_letter_date', 'date')}
          {field('Initial Deadline', 'initial_deadline', 'date')}
        </div>

        {/* Importer */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="font-semibold mb-4 text-gray-700">Importer</h2>
          {field('Importer Name', 'importer_name')}
          {field('EORI', 'importer_eori')}
          {field('Agent', 'agent_name')}
        </div>

        {/* C18 Details */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="font-semibold mb-4 text-gray-700">C18 Details</h2>
          {field('C18 Number', 'c18_number')}
          {field('C18 Letter Date', 'c18_letter_date', 'date')}
          {field('C18 Deadline', 'c18_deadline', 'date')}
          {field('C18 Amount', 'c18_amount', 'number')}
        </div>

        {/* Job References */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="font-semibold mb-4 text-gray-700">Job References</h2>
          <div className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">
            {caseData.job_refs ? (
              <div className="flex gap-2 flex-wrap">
                {caseData.job_refs.split(',').map((ref, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                    {ref.trim()}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        </div>
      </div>

      {/* Appeal Section */}
      {(caseData.status === 'appealing' || caseData.appeal_reference) && (
        <div className="bg-white rounded-lg border p-6 mb-6">
          <h2 className="font-semibold mb-4 text-gray-700">Appeal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {field('Appeal Reference (TC Code)', 'appeal_reference')}
            {field('Appeal Date', 'appeal_date', 'date')}
            {field('Handed to Agent Date', 'handed_to_agent_date', 'date')}
          </div>
        </div>
      )}

      {/* Agent Communication Section */}
      {poa && (
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6 mb-6">
          <h2 className="font-semibold mb-4 text-gray-700">Agent & POA Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Agent Name</p>
              <p className="text-sm font-semibold">{poa.agent_name}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Signatory</p>
              <p className="text-sm">{poa.signatory_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">POA Valid Until</p>
              <p className="text-sm">{new Date(poa.valid_until).toLocaleDateString('en-GB')}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">POA Status</p>
              <span className="inline-block px-2 py-1 rounded text-xs bg-green-100 text-green-800">{poa.status}</span>
            </div>
          </div>
          <Link to={`/customs/poa/${poa.id}`} className="text-blue-600 hover:underline text-sm mt-4 inline-block">
            View Full POA →
          </Link>
        </div>
      )}

      {/* Timeline / Notes Section */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="font-semibold mb-4 text-gray-700">Timeline & Notes</h2>

        {/* Add Note Form */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className={LABEL_CLS}>Add Note</label>
          <textarea
            className={INPUT_CLS + ' mb-3'}
            rows={2}
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note about this case..."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLS}>Action Type</label>
              <select className={INPUT_CLS} value={noteActionType} onChange={e => setNoteActionType(e.target.value)}>
                <option value="initial_email_sent">Initial Email Sent</option>
                <option value="reminder_sent">Reminder Sent</option>
                <option value="c18_received">C18 Received</option>
                <option value="appeal_submitted">Appeal Submitted</option>
                <option value="paid">Paid</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddNote}
                disabled={addingNote || !noteText.trim()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {addingNote ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>

        {/* Notes List */}
        {notes.length > 0 ? (
          <div className="space-y-4">
            {notes.map(note => (
              <div key={note.id} className="pb-4 border-b last:border-b-0">
                <div className="flex items-start gap-3 mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_TYPE_STYLES[note.action_type] || ACTION_TYPE_STYLES.other}`}>
                    {ACTION_TYPE_LABELS[note.action_type] || note.action_type || 'Note'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(note.created_at).toLocaleDateString('en-GB')} {new Date(note.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {note.created_by && <span className="text-xs text-gray-500">by {note.created_by}</span>}
                </div>
                <p className="text-sm text-gray-700">{note.note_text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No notes yet</p>
        )}
      </div>
    </div>
  )
}
